import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Container,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Chip,
  Stack,
  useTheme
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { fetchChatbotData, saveChatbotConfig } from '../services/api';

function Notifications() {
  const theme = useTheme();
  const { user } = useUser();
  const isReadOnly = user?.rol === 'readonly';
    const [notificationText, setNotificationText] = useState('Mandar notificación cuando se reciba un correo electrónico o un número de teléfono. También cuando el nivel de urgencia sea superior a 6.');
  const [error, setError] = useState(null);
  const [originalText, setOriginalText] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [exceedsLimit, setExceedsLimit] = useState(false);
  const { selectedChatbot } = useSelectedChatbot();
  
  // Estado para los correos electrónicos
  const [emailList, setEmailList] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Obtener el límite de caracteres personalizado si existe en las opciones del producto
  const MAX_CHAR_LIMIT = (() => {
    // Valor por defecto para notificaciones
    const DEFAULT_LIMIT = 3000;
    
    // Buscar el producto seleccionado
    const selectedProduct = user?.products?.find(p => p.code === selectedChatbot);
    
    // Extraer el tamaño de notificaciones personalizado de las opciones si existe
    if (selectedProduct?.options) {
      // Buscar NOTIFICATIONS_SIZE= en cualquier posición de la cadena
      const sizeMatch = selectedProduct.options.match(/NOTIFICATIONS_SIZE=(\d+)/i);
      if (sizeMatch && sizeMatch[1]) {
        const customLimit = parseInt(sizeMatch[1]);
        if (!isNaN(customLimit) && customLimit > 0) {
          return customLimit;
        }
      }
    }
    
    return DEFAULT_LIMIT;
  })();

  // Cargar configuración de notificaciones
  useEffect(() => {
    const loadNotifications = async () => {
      const defaultText = 'Mandar notificación cuando se reciba un correo electrónico o un número de teléfono. También cuando el nivel de urgencia sea superior a 6.';
      
      if (!selectedChatbot) {
        setNotificationText(defaultText);
        setOriginalText('');
        setError(null);
        setIsModified(false);
        setCharCount(0);
        setEmailList([]);
        return;
      }

      try {
        // Cargar datos completos del chatbot
        const chatbotData = await fetchChatbotData(selectedChatbot);
        
        // Verificar si existe la sección de notificaciones
        if (chatbotData && chatbotData.notifications) {
          // Cargar instrucciones de notificaciones
          if (chatbotData.notifications.instructions) {
            setNotificationText(chatbotData.notifications.instructions);
            setOriginalText(chatbotData.notifications.instructions);
            setCharCount(chatbotData.notifications.instructions.length);
            setExceedsLimit(chatbotData.notifications.instructions.length > MAX_CHAR_LIMIT);
          } else {
            setNotificationText(defaultText);
            setOriginalText(defaultText);
            setCharCount(defaultText.length);
          }
          
          // Cargar lista de correos
          if (chatbotData.notifications.emails && typeof chatbotData.notifications.emails === 'string') {
            // Si los emails están como string separado por comas
            const emailArray = chatbotData.notifications.emails
              .split(',')
              .map(email => email.trim())
              .filter(email => email.length > 0);
            setEmailList(emailArray);
          } else {
            setEmailList([]);
          }
        } else {
          // Valores por defecto si no existe la sección de notificaciones
          setNotificationText(defaultText);
          setOriginalText(defaultText);
          setCharCount(defaultText.length);
          setEmailList([]);
        }
        
        setIsModified(false);
      } catch (err) {
        console.error('Error loading notifications:', err);
        setError('Error al cargar las notificaciones');
        setNotificationText(defaultText);
        setOriginalText(defaultText);
        setCharCount(defaultText.length);
        setEmailList([]);
      }
    };

    loadNotifications();
  }, [selectedChatbot]);

  const handleTextChange = (event) => {
    const newText = event.target.value;
    setNotificationText(newText);
    setCharCount(newText.length);
    setExceedsLimit(newText.length > MAX_CHAR_LIMIT);
    setIsModified(newText !== originalText);
  };

  // Validar formato de email
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  // Añadir un nuevo email a la lista
  const handleAddEmail = () => {
    if (!newEmail.trim()) {
      setEmailError('El correo no puede estar vacío');
      return;
    }

    if (!validateEmail(newEmail)) {
      setEmailError('Formato de correo inválido');
      return;
    }

    if (emailList.includes(newEmail)) {
      setEmailError('Este correo ya está en la lista');
      return;
    }

    setEmailList([...emailList, newEmail]);
    setNewEmail('');
    setEmailError('');
    setIsModified(true);
  };

  // Eliminar un email de la lista
  const handleRemoveEmail = (emailToRemove) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
    setIsModified(true);
  };

  const handleSaveChanges = async () => {
    if (isReadOnly) return;
    if (!selectedChatbot || !notificationText.trim() || exceedsLimit) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Obtener los datos actuales del chatbot
      const chatbotData = await fetchChatbotData(selectedChatbot);
      
      // Añadir la sección de notificaciones al objeto
      const updatedData = {
        ...chatbotData,
        notifications: {
          instructions: notificationText,
          emails: emailList.join(', ')
        }
      };
      
      // Guardar el objeto actualizado usando saveChatbotConfig
      await saveChatbotConfig(selectedChatbot, updatedData);
      
      setOriginalText(notificationText);
      setIsModified(false);
      setSaveSuccess(true);

      // Mostrar mensaje de éxito
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving notifications:', err);
      setSaveError('Error al guardar las notificaciones');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ p: 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Notificaciones
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Configura los criterios para el envío de notificacíones.
          </Typography>
        </Paper>

        {/* Editor de Notificaciones */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            minHeight: '200px',
            height: '250px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            mb: 3,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
            boxShadow: theme.palette.mode === 'dark' ? '0 18px 40px rgba(0,0,0,0.20)' : '0 16px 36px rgba(15,23,42,0.07)'
          }}
        >
          <Box sx={{
            px: 2,
            py: 1.35,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '-0.01em'
              }}
            >
              Reglas de Notificación
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: exceedsLimit ? theme.palette.error.main : theme.palette.text.secondary,
                  marginRight: '10px',
                  fontWeight: exceedsLimit ? 'bold' : 'normal',
                  fontSize: '0.78rem',
                  opacity: 0.9,
                }}
              >
                {charCount}/{MAX_CHAR_LIMIT} caracteres
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
            {selectedChatbot ? (
              <Box sx={{ p: 2, height: '100%', width: '100%', display: 'flex', minHeight: 0 }}>
                <Box
                  component="textarea"
                  value={notificationText}
                  onChange={handleTextChange}
                  disabled={isReadOnly}
                  spellCheck={false}
                  placeholder="Escribe aquí los criterios para enviar notificaciones..."
                  sx={{
                    flex: 1,
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    resize: 'none',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    color: theme.palette.text.primary,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    fontSize: '0.92rem',
                    lineHeight: 1.7,
                    padding: '18px 18px 20px',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.20)'} transparent`,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.20)',
                      borderRadius: '999px',
                    },
                  }}
                />
              </Box>
            ) : (
              <Typography sx={{ p: 2, color: theme.palette.text.secondary }}>
                Selecciona un asistente para configurar sus notificaciones
              </Typography>
            )}
          </Box>
        </Paper>
        
        {/* Lista de Correos */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            minHeight: '150px',
            height: 'auto',
            maxHeight: emailList.length > 3 ? 'calc(100vh - 550px)' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.action.hover,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Destinatarios de Notificaciones
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              gap: 1
            }}>
              <TextField
                fullWidth
                size="small"
                label="Añadir correo electrónico"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailError('');
                }}
                error={!!emailError}
                helperText={emailError}
                disabled={isReadOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: theme.palette.text.primary,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                }}
              />
              <IconButton 
                onClick={handleAddEmail}
                disabled={isReadOnly}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ my: 2, backgroundColor: theme.palette.divider }} />
            
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              Correos configurados:
            </Typography>
            
            <Box sx={{ 
              maxHeight: emailList.length > 3 ? '200px' : 'auto', 
              overflowY: 'auto',
              p: 1,
              border: emailList.length > 0 ? `1px solid ${theme.palette.divider}` : 'none',
              borderRadius: 1,
              backgroundColor: emailList.length > 0 ? theme.palette.action.hover : 'transparent'
            }}>
              {emailList.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {emailList.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={isReadOnly ? undefined : () => handleRemoveEmail(email)}
                      sx={{
                        backgroundColor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderRadius: '4px',
                        m: 0.5,
                        '& .MuiChip-deleteIcon': {
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: theme.palette.error.main,
                          },
                        },
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: theme.palette.text.secondary, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No hay correos configurados
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Botón de guardar centrado */}
        {isModified && (
          <Box
            sx={{
              mt: 3,
              mb: 3,
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              variant="contained"
              onClick={isReadOnly ? undefined : handleSaveChanges}
              startIcon={isSaving ? <CircularProgress size={16} sx={{ color: theme.palette.primary.contrastText }} /> : <SaveIcon sx={{ color: theme.palette.primary.contrastText }} />}
              disabled={isSaving || exceedsLimit || isReadOnly}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  borderColor: theme.palette.primary.dark,
                },
                height: '60px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                width: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '& .MuiButton-startIcon': {
                  marginRight: 1,
                  '& svg': {
                    fontSize: '1.2rem'
                  }
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                  borderColor: theme.palette.action.disabledBackground,
                }
              }}
            >
              {isSaving ? 'Guardando...' : exceedsLimit ? 'Límite excedido' : 'Guardar Cambios'}
            </Button>
          </Box>
        )}

        {/* Mensajes de estado */}
        <Snackbar
          open={saveSuccess}
          autoHideDuration={6000}
          onClose={() => setSaveSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSaveSuccess(false)}
            severity="success"
            sx={{
              width: '100%',
              backgroundColor: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              '& .MuiAlert-icon': {
                color: theme.palette.getContrastText(theme.palette.success.main)
              }
            }}
          >
            Notificaciones guardadas correctamente
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!saveError}
          autoHideDuration={6000}
          onClose={() => setSaveError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSaveError(null)}
            severity="error"
            sx={{
              width: '100%',
              backgroundColor: theme.palette.error.main,
              color: theme.palette.getContrastText(theme.palette.error.main),
              '& .MuiAlert-icon': {
                color: theme.palette.getContrastText(theme.palette.error.main)
              }
            }}
          >
            {saveError}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default Notifications;
